// ============================================================
// THE HITS ON A PLATE (plan body-map H1; the bench's four signatures, ruled
// 2026-10-08). What one hit plays on the body it struck, at its own
// location, by its damage type (when, in a burst: ./burst) -
//
//   Energy    a white-hot bloom that cools slowly through amber
//   Impact    a hard jolt that rings, cold blue echoes above and below
//   Explosive blown outward in pieces and drawn back; the figure thrown
//   Rending   sliced, the slices shoved apart
//
// - and a critical as the same signature heavier (every peak doubled) and
// longer, the figure thrown, and on a person a strobe. The colours and the
// location's own move are CSS (PlateFx.module.css); the filter's numbers,
// the throw and the strobe are here, for the plate's clock to write.
//
// Pure. Zero React dependency. Seconds; frame units.
// ============================================================

import type { DamageCode } from '../impacts';
import type { VehicleFacing } from '../vehicleFacing';
import { seeded } from './hash';
import { lerp, progress } from '../display/timeline';

/** One location's hit, as the plate plays it. */
export interface PlateHitPlay {
  location: string;
  type: DamageCode;
  crit: boolean;
}

/** How long each signature plays, plain and as a critical (its CSS animation's duration). */
const SECONDS: Record<DamageCode, [number, number]> = { E: [2.2, 3.4], I: [1.1, 1.8], X: [1.8, 2.8], R: [1.6, 2.6] };

export const hitSeconds = (type: DamageCode, crit: boolean) => SECONDS[type][crit ? 1 : 0];

/** The throw's length, and the strobe's: three flashes of 0.42 s. */
const THROW_SECONDS = 0.7;
const STROBE = { period: 0.42, times: 3 };

/** One animated value of a hit's filter: the element (`data-g`), its attribute, its peak and how long it fades. */
interface FilterBeat {
  key: string;
  attr: string;
  peak: number;
  seconds: number;
}

/** Each signature's filter: what it pushes, how far, how long (the bench's peaks). */
const FILTER: Record<DamageCode, FilterBeat[]> = {
  E: [
    { key: 'hitE-d', attr: 'scale', peak: 30, seconds: 2.2 },
    { key: 'hitE-b', attr: 'stdDeviation', peak: 38, seconds: 2.2 },
  ],
  I: [
    { key: 'hitI-up', attr: 'dy', peak: -90, seconds: 0.9 },
    { key: 'hitI-down', attr: 'dy', peak: 90, seconds: 0.9 },
  ],
  X: [
    { key: 'hitX-d', attr: 'scale', peak: 340, seconds: 1.4 },
    { key: 'hitX-b', attr: 'stdDeviation', peak: 24, seconds: 0.8 },
  ],
  R: [{ key: 'hitR-d', attr: 'scale', peak: 260, seconds: 1.6 }],
};

/** Rending's slices reshuffle: its noise's seed steps through these, a tenth of a second each, four times over. */
const SLICE_SEEDS = [9, 3, 14, 6];
const SLICE = { step: 0.1, seconds: 1.6 };

/** From the peak through half of it to nothing, evenly over `seconds`; nothing after. */
function fade(peak: number, seconds: number, t: number): number {
  const p = progress(t, 0, seconds);
  return p < 0.5 ? peak * (1 - 0.9 * p) : peak * 0.55 * (2 - 2 * p);
}

/** A hit's filter `t` seconds in: each element's attributes (`amp` 2 for a critical: every peak doubled). */
export function hitFilterAt(
  type: DamageCode,
  amp: number,
  t: number,
): { key: string; attrs: Record<string, number> }[] {
  const beats = FILTER[type].map((b) => ({ key: b.key, attrs: { [b.attr]: fade(b.peak * amp, b.seconds, t) } }));
  if (type !== 'R') return beats;
  // (+1e-9: 0.3 / 0.1 is 2.9999999999999996 in floating point)
  const step = t < SLICE.seconds ? Math.floor(Math.max(0, t) / SLICE.step + 1e-9) : 0;
  return [...beats, { key: 'hitR-n', attrs: { seed: SLICE_SEEDS[step % SLICE_SEEDS.length] } }];
}

const easeOut = (x: number) => 1 - (1 - x) ** 3;

type Vec = [number, number];

/** A knock `t` seconds in: out to `first`, back past rest to `back` at 30%, settled - each leg eased out. */
function kickAt(t: number, first: Vec, back: Vec): { dx: number; dy: number } {
  const p = progress(t, 0, THROW_SECONDS);
  const [from, to, f] = p < 0.3 ? [first, back, easeOut(p / 0.3)] : [back, [0, 0], easeOut((p - 0.3) / 0.7)];
  // (+ 0: never -0 - a transform written as "-0.0" is still rest, but reads as a move)
  const at = (k: 0 | 1) => lerp(from[k], to[k], f) + 0;
  return { dx: at(0), dy: at(1) };
}

/** A person thrown `t` seconds in, frame units: knocked up and away (doubled for a critical), back past rest, settled. */
export const throwAt = (t: number, amp: number) => kickAt(t, [60 * amp, -30 * amp], [-24, 8]);

/** A vehicle's side, as its plate labels it. */
export type VehicleSide = 'front' | 'rear' | 'left' | 'right';

/**
 * The side a vehicle was struck on: the facing in play, and for a side hit the left or the right - which one the
 * impact's id decides, so every screen plays the same side and a replay never flips it.
 */
export function struckSide(facing: VehicleFacing, id: string): VehicleSide {
  if (facing !== 'side') return facing;
  return seeded(id)('side') < 0.5 ? 'left' : 'right';
}

/** From each side, which way is in: a unit vector toward the vehicle seen from above (screen y down). */
const INWARD: Record<VehicleSide, Vec> = { front: [0, 1], rear: [0, -1], left: [1, 0], right: [-1, 0] };

/** Which way in, as the plate shows it: seen from the front (the walker), every direction mirrors. */
function inward(side: VehicleSide, view: 'top' | 'front'): Vec {
  const [x, y] = INWARD[side];
  return view === 'front' ? [-x, -y] : [x, y];
}

/** A vehicle knocked away from the side struck `t` seconds in (doubled for a critical), and back. */
export function vehicleThrowAt(t: number, amp: number, side: VehicleSide, view: 'top' | 'front') {
  const [x, y] = inward(side, view);
  return kickAt(t, [70 * amp * x, 70 * amp * y], [-20 * x, -20 * y]);
}

/** One band of the wave: its rect in frame units, and how bright. */
export interface WaveBand {
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
}

/** The wave: its bright core and its fainter, wider trail (width, brightness), and how long it takes to cross. */
const WAVE = {
  bands: [
    [170, 1],
    [560, 0.5],
  ] as const,
  seconds: 0.75,
  overrun: 700,
  margin: 60,
};

/**
 * The wave across a struck vehicle `t` seconds in: a bright band crossing its own lines from the side struck, a
 * fainter wider trail behind it, both fading as they go (the canvas's `facingBeat`). `box` is the drawing's.
 */
export function waveAt(t: number, side: VehicleSide, view: 'top' | 'front', box: readonly number[]): WaveBand[] {
  const p = progress(t, 0, WAVE.seconds);
  const axis = waveAxis(side, view, box);
  const [lo, hi] = [axis.lo - WAVE.margin, axis.hi + WAVE.margin];
  const travel = (hi - lo + WAVE.overrun) * p;
  const lead = axis.forward ? lo + travel : hi - travel;
  // Not begun (a burst's glances come first), or over: unseen.
  const fade = t >= 0 && p < 1 ? 1 - 0.6 * p : 0;
  return WAVE.bands.map(([w, op]) => axis.band({ at: axis.forward ? lead - w : lead, w, opacity: op * fade }, box));
}

/** Which way the wave travels over the drawing: across or down, from which end, over what span. */
function waveAxis(side: VehicleSide, view: 'top' | 'front', box: readonly number[]) {
  const [ix, iy] = inward(side, view);
  return ix !== 0
    ? { forward: ix > 0, lo: box[0], hi: box[2], band: acrossBand }
    : { forward: iy > 0, lo: box[1], hi: box[3], band: downBand };
}

interface Band {
  at: number;
  w: number;
  opacity: number;
}

/** A band crossing from side to side: as tall as the drawing, and some. */
const acrossBand = ({ at, w, opacity }: Band, box: readonly number[]): WaveBand => ({
  x: at,
  y: box[1] - 400,
  width: w,
  height: box[3] - box[1] + 800,
  opacity,
});

/** A band crossing from end to end: as wide as the drawing, and some. */
const downBand = ({ at, w, opacity }: Band, box: readonly number[]): WaveBand => ({
  x: box[0] - 400,
  y: at,
  width: box[2] - box[0] + 800,
  height: w,
  opacity,
});

/** A critical's strobe `t` seconds in: the figure's brightness - bright, dark, bright, three times - then null. */
export function strobeAt(t: number): number | null {
  if (t < 0 || t >= STROBE.period * STROBE.times) return null;
  const third = Math.floor(((t % STROBE.period) / STROBE.period) * 3);
  return [2.4, 0.35, 1.8][third];
}
